import { NarrativeItem, NarrativeStore } from '@assets/model/NarrativeStore';
import { SideChatStore } from '@assets/model/SideChatStore';
import { useStore, useStoreUI } from '@assets/model/Store';
import classNames from 'classnames';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { LoadingAnim } from '../Loading/LoadingAnim';
import {
    ScrollPanel,
    ScrollPanelStore
} from '../Panel/ScrollPanel/ScrollPanel';
import { Flex } from '../UI/Flex/Flex';
import * as svgs from './icons/*.svg';
import {
    ReplyMessageFactory,
    ReplyMessageType
} from './messages/ReplyMessageFactory';
import UserMessage from './messages/UserMessage/UserMessage';
import WaitingMessage from './messages/WaitingMessage/WaitingMessage';
import * as style from './SideChat.module.less';

interface SideChatProps {
    narStore: NarrativeStore;
}

const SideChat = observer((p: SideChatProps) => {
    const { narStore } = p;
    const ui = useStoreUI();
    const store = useStore();
    const [chatStore, setChatStore] = useState<SideChatStore>(null);
    const [isMinimized, setIsMinimized] = useState(true);
    const [scrollApi] = useState(() => new ScrollPanelStore());
    const [messsagesDisplayed, setMessagesDisplayed] = useState(0);

    useEffect(() => {
        if (chatStore) {
            chatStore.unregister();
        }
        setMessagesDisplayed(0);
        setChatStore(new SideChatStore(store.selectedReportId));
    }, [store.selectedReportId]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (
            chatStore &&
            chatStore.status == 'ready' &&
            chatStore.chatMessages.length != messsagesDisplayed
        ) {
            setMessagesDisplayed(chatStore.chatMessages.length);
            scrollApi.eventScrollEnd += 1;
        }
    }, [messsagesDisplayed, chatStore?.chatMessages.length || 0]);

    useEffect(() => {
        if (ui.sideChatSelectedContext == null) {
            return;
        }
        if (isMinimized) {
            setIsMinimized(false);
        }
    }, [ui.sideChatSelectedContext]);

    const handleSend = () => {
        if (!inputRef.current) return;

        if (isWaitingReply) return;

        const text = inputRef.current.innerText.trim();

        if (!text) return;

        chatStore.send_message(text, narrItem.fullContextPath);
        inputRef.current.innerText = '';
    };

    const handleKeyDown = e => {
        // Check for Enter key without Shift held down
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Stop native newline behavior
            handleSend();
        }
    };

    let narrItem: NarrativeItem = null;
    if (ui.sideChatSelectedContext) {
        narrItem = narStore.getNarrativeItemById(ui.sideChatSelectedContext);
    }

    let displayContextId = '';
    let displayName = '';
    let contextType = '';

    if (narrItem) {
        displayContextId = narrItem.ui_displayIdGenerated;
        displayName = narrItem?.ui_display ?? 'None';
        contextType = narrItem.level.replace('L', 'Level ');

        const char_limit = 35;
        if (displayName.length > char_limit) {
            displayName = displayName.substring(0, char_limit) + '...';
        }
    }

    const headerTitle = `${displayContextId} ${displayName}`;
    const headerContextType = contextType;
    const onMinMaxClick = () => {
        setIsMinimized(!isMinimized);
    };

    const isLoading = chatStore == null || chatStore.status == 'loading';

    let isWaitingReply = false;
    const msgs = chatStore?.chatMessages;
    if (msgs && msgs.length > 0) {
        const isMe = msgs[msgs.length - 1].isMe;
        if (isMe) {
            isWaitingReply = true;
        }
    }

    const headerEl = (
        <Flex
            className={classNames(style.header)}
            alignItems="center"
            fillCell={true}
        >
            <div className={style.header_title}>AI Chat</div>
            <div
                className={classNames(style.header_breadcrumb, {
                    [style.sideChatLabelHighlight]:
                        ui.sideChatSelectedContext != null
                })}
                key={displayContextId}
            >
                <Flex
                    justify="between"
                    alignItems="center"
                    style={{ width: '100%' }}
                >
                    <div>{headerTitle}</div>
                    <div className={style.header_contextType}>
                        {headerContextType}
                    </div>
                </Flex>
            </div>
            <div
                className={style.header_min_max_button}
                onClick={() => {
                    onMinMaxClick();
                }}
            >
                <img src={isMinimized ? svgs['maximize'] : svgs['minimize']} />
            </div>
        </Flex>
    );

    const chatInputEl = (
        <div className={style.input}>
            <div className={style.input_container}>
                <Flex fillCell={true} style={{ justifyContent: 'center' }}>
                    <div className={style.input_textbox}>
                        <div
                            ref={inputRef}
                            contentEditable="true"
                            placeholder="Type something here..."
                            className={style.fake_textarea}
                            onKeyDown={handleKeyDown}
                        ></div>
                    </div>
                    <div
                        className={classNames(style.input_chat_arrow, {
                            [style.input_chat_arrow_disabled]: isWaitingReply
                        })}
                        onClick={handleSend}
                    >
                        <img src={svgs['chat_arrow']} />
                    </div>
                </Flex>
            </div>
        </div>
    );
    const generateChatHistoryEl = () => {
        const makeRow = (key, el) => {
            return (
                <div
                    key={'flex_' + key}
                    style={{
                        width: '100%',
                        display: 'flex'
                    }}
                >
                    {el}
                </div>
            );
        };

        const messagesEl = chatStore.chatMessages.map((m, i) => {
            const msgId = `msg_${i}_${m.type}`;
            if (m.isMe) {
                return makeRow(msgId, <UserMessage key={msgId} message={m} />);
            } else {
                return makeRow(
                    msgId,
                    ReplyMessageFactory.createMessageByType(
                        m.type as ReplyMessageType,
                        m,
                        msgId,
                        narStore
                    )
                );
            }
        });

        if (isWaitingReply) {
            const loadingMessageId = 'loading_msg_id';
            messagesEl.push(
                makeRow(
                    loadingMessageId,
                    <WaitingMessage key={loadingMessageId} />
                )
            );
        } else {
            messagesEl.push(
                <div key="gap" style={{ width: '100%', height: '1px' }}></div>
            );
        }

        return (
            <Flex fillCell={true}>
                <ScrollPanel api={scrollApi} className={style.scrollContainer}>
                    <Flex
                        className={style.messagesContainer}
                        direction="column"
                        wrap="wrap"
                        alignItems="end"
                    >
                        {messagesEl}
                    </Flex>
                </ScrollPanel>
            </Flex>
        );
    };

    const generateLoadingChatEl = () => {
        return (
            <Flex style={{ width: '100%', flexGrow: 1 }} justify="center">
                <LoadingAnim />{' '}
            </Flex>
        );
    };

    const chatHistoryEl = isLoading
        ? generateLoadingChatEl()
        : generateChatHistoryEl();

    const sideChatClasses = classNames(style.sideChatContainer, {
        [style.sideChatContainerMaximized]: !isMinimized
    });

    return (
        <div
            className={sideChatClasses}
            onClick={() => {
                if (isMinimized && ui.sideChatSelectedContext != null) {
                    setIsMinimized(false);
                }
            }}
            style={{ cursor: isMinimized ? 'pointer' : 'default' }}
        >
            <Flex fillCell={true} direction="column">
                <div
                    className={classNames(style.headerContainer, {
                        [style.sideChatNoSelection]:
                            ui.sideChatSelectedContext == null
                    })}
                >
                    {headerEl}
                </div>
                {!isMinimized && (
                    <>
                        {chatHistoryEl}
                        {chatInputEl}
                    </>
                )}
            </Flex>
        </div>
    );
});

export default SideChat;
